import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  StaffDaysWithAvailability,
  staffWeekAvailability,
} from "~/api/adminProviderSearch";
import { CalendarDay } from "~/shared/types";
import { today } from "~/shared/utils/dates";
import { timezone } from "~/shared/utils/timezone";

interface UseStaffSearchCalendarParams {
  groupId: string;
  conditions: string[];
  state: string;
  office: string;
}

interface UseStaffSearchCalendarReturn {
  calendarDays: CalendarDay[];
  currentStartDate: Date;
  navigatePrevWeek: () => void;
  navigateNextWeek: () => void;
  navigateToDate: (date: Date) => void;
  canNavigatePrev: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
  availabilityForNextMonth?: boolean;
  isMobile: boolean;
  firstAvailableDay: Date | null;
  isNavigating: boolean;
}

const getWeekDays = (startDate: Date): CalendarDay[] => {
  const days: CalendarDay[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    days.push({ date: new Date(date), available: false });
  }
  return days;
};

const formatDate = (date: Date): string => date.toISOString().split("T")[0];

const getWeekStartDate = (targetDate: Date): Date => {
  const startDate = new Date(targetDate);
  const dayOfWeek = startDate.getDay();
  startDate.setDate(startDate.getDate() - dayOfWeek);
  return startDate;
};

// Helper to compare dates (ignoring time)
const isSameDay = (date1: Date, date2: Date): boolean => {
  return formatDate(date1) === formatDate(date2);
};

/**
 * Staff/group-driven copy of `useCalendarDays` (see `~/shared/hooks/useCalendarDays`)
 * for the backoffice provider-search preview. Unlike the patient hook, this
 * takes its search params explicitly (`groupId`, `conditions`, `state`, `office`)
 * instead of reading a patient record off `useSignUpStore` — the preview has no
 * patient. Same week-navigation and first-available-day auto-select behavior.
 */
export const useStaffSearchCalendar = (
  { groupId, conditions, state, office }: UseStaffSearchCalendarParams,
  onFirstAvailableDayFound?: (date: Date | null) => void,
): UseStaffSearchCalendarReturn => {
  const [targetStartDate, setTargetStartDate] = useState<Date>(today);
  const [displayedStartDate, setDisplayedStartDate] = useState<Date>(today);
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>(
    getWeekDays(today),
  );
  const [isMobile, setIsMobile] = useState(false);
  const [firstAvailableDay, setFirstAvailableDay] = useState<Date | null>(null);
  const [isAutoNavigating, setIsAutoNavigating] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const prevFirstAvailableDayRef = useRef<Date | null>(null);
  const autoNavAttemptsRef = useRef(0);
  // Deliberately NOT raised alongside useCalendarDays' 22. The staff endpoint
  // (StaffWeekAvailabilityAction) derives availabilityForNextMonth from the
  // CURRENT week's dates alone — it runs no horizon probe — so the loop below
  // stops at the first empty window no matter how high this goes. Reach here is
  // 8 steps: ~24 days on mobile (3/step), ~56 on desktop (7/step), not 2 months.
  // Giving the admin calendar the funnel's far-out reach means teaching the staff
  // action to probe the horizon first; until then this number is not the limit.
  const MAX_AUTO_NAV_ATTEMPTS = 8;

  const canSearch =
    groupId !== "" && conditions.length > 0 && state !== "" && office !== "";

  // Track if we're in a navigation transition (target differs from displayed)
  const isNavigating = useMemo(() => {
    return !isSameDay(targetStartDate, displayedStartDate);
  }, [targetStartDate, displayedStartDate]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Reset when search params change
  useEffect(() => {
    setTargetStartDate(today);
    setDisplayedStartDate(today);
    setCalendarDays(getWeekDays(today));
    prevFirstAvailableDayRef.current = null;
    setIsAutoNavigating(false);
    setIsInitialLoad(true);
    setFirstAvailableDay(null);
    autoNavAttemptsRef.current = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, conditions, state, office]);

  const {
    data: availableDays,
    isLoading,
    error: queryError,
    isFetching,
  } = useQuery({
    queryKey: [
      "staffDaysWithAvailability",
      groupId,
      conditions,
      state,
      office,
      formatDate(targetStartDate),
    ],
    queryFn: async () => {
      const startDate = new Date(
        Math.max(targetStartDate.getTime(), today.getTime()),
      );
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);

      if (!canSearch) {
        return {} as StaffDaysWithAvailability;
      }

      return staffWeekAvailability({
        groupId,
        conditions,
        state,
        preferredOfficeVisit: office,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        timezone,
      });
    },
    enabled: canSearch,
    // Keep previous data while fetching to prevent flicker
    placeholderData: (previousData) => previousData,
  });

  // Update calendar days only when we have fresh data for the target date
  useEffect(() => {
    // Don't update if still fetching data for a new target
    if (isFetching && isNavigating) {
      return;
    }

    // Only update display when we have data matching the target
    if (!availableDays || !canSearch) {
      return;
    }

    const days = getWeekDays(targetStartDate);
    days.forEach((day) => {
      day.available = (availableDays.dates?.[formatDate(day.date)] || 0) > 0;
    });

    setCalendarDays(days);
    setDisplayedStartDate(targetStartDate);

    // Find first available day
    const firstAvailable = days.find((day) => day.available);
    const newFirstAvailableDay = firstAvailable ? firstAvailable.date : null;

    // Only trigger callback if the first available day actually changed
    const prevDate = prevFirstAvailableDayRef.current;
    const hasChanged =
      (!prevDate && newFirstAvailableDay) ||
      (prevDate && !newFirstAvailableDay) ||
      (prevDate &&
        newFirstAvailableDay &&
        !isSameDay(prevDate, newFirstAvailableDay));

    if (hasChanged) {
      setFirstAvailableDay(newFirstAvailableDay);
      prevFirstAvailableDayRef.current = newFirstAvailableDay;
      onFirstAvailableDayFound?.(newFirstAvailableDay);
    }

    // Auto-navigate to find availability
    if (
      !firstAvailable &&
      canSearch &&
      (isInitialLoad || isAutoNavigating) &&
      availableDays?.availabilityForNextMonth === true &&
      autoNavAttemptsRef.current < MAX_AUTO_NAV_ATTEMPTS
    ) {
      setIsAutoNavigating(true);
      autoNavAttemptsRef.current += 1;

      const nextWeekStart = new Date(targetStartDate);
      const daysToMove = isMobile ? 3 : 7;
      nextWeekStart.setDate(nextWeekStart.getDate() + daysToMove);
      setTargetStartDate(nextWeekStart);
    } else if (firstAvailable) {
      // Found availability, stop auto-navigating
      setIsAutoNavigating(false);
      setIsInitialLoad(false);
      autoNavAttemptsRef.current = 0;
    } else if (
      availableDays?.availabilityForNextMonth === false ||
      autoNavAttemptsRef.current >= MAX_AUTO_NAV_ATTEMPTS
    ) {
      // No more availability or hit max attempts
      setIsAutoNavigating(false);
      setIsInitialLoad(false);
    }
  }, [
    targetStartDate,
    canSearch,
    availableDays,
    isAutoNavigating,
    isFetching,
    isNavigating,
    isMobile,
    isInitialLoad,
    onFirstAvailableDayFound,
  ]);

  const navigateWeek = useCallback(
    (direction: "prev" | "next") => {
      setTargetStartDate((current) => {
        const newStartDate = new Date(current);
        const daysToMove = isMobile ? 3 : 7;
        newStartDate.setDate(
          newStartDate.getDate() +
            (direction === "prev" ? -daysToMove : daysToMove),
        );

        // Don't navigate before today
        if (direction === "prev" && newStartDate.getTime() < today.getTime()) {
          return current;
        }

        return newStartDate;
      });

      // Stop any auto-navigation when user manually navigates
      setIsAutoNavigating(false);
      setIsInitialLoad(false);
      autoNavAttemptsRef.current = 0;
    },
    [isMobile],
  );

  const navigateToDate = useCallback((targetDate: Date) => {
    const weekStartDate = getWeekStartDate(targetDate);
    setTargetStartDate(weekStartDate);
    setIsAutoNavigating(false);
    setIsInitialLoad(false);
    autoNavAttemptsRef.current = 0;
  }, []);

  const canNavigatePrev = useMemo(() => {
    return targetStartDate.getTime() > today.getTime();
  }, [targetStartDate]);

  return {
    calendarDays,
    currentStartDate: displayedStartDate,
    navigatePrevWeek: () => navigateWeek("prev"),
    navigateNextWeek: () => navigateWeek("next"),
    navigateToDate,
    canNavigatePrev,
    isLoading: isLoading || (isFetching && isNavigating) || isAutoNavigating,
    isFetching,
    error: queryError instanceof Error ? queryError.message : null,
    availabilityForNextMonth: availableDays?.availabilityForNextMonth,
    isMobile,
    firstAvailableDay,
    isNavigating,
  };
};
